import { MessagePart } from "@/interfaces/Chat";
export const plainText = (parts: MessagePart[]): string =>
  parts.map((p) => (p.type === "text" ? p.text : `@${p.label}`)).join("");
export const compactParts = (parts: MessagePart[]): MessagePart[] =>
  parts.reduce<MessagePart[]>((result, part) => {
    const previous = result[result.length - 1];
    if (part.type === "text" && !part.text) return result;
    if (part.type === "text" && previous?.type === "text")
      previous.text += part.text;
    else result.push({ ...part });
    return result;
  }, []);
export const removeMentionAt = (
  parts: MessagePart[],
  index: number
): MessagePart[] =>
  compactParts(parts.filter((p, i) => i !== index || p.type !== "mention"));
export type TextEditRange = { start: number; end: number };
/** Native textarea offsets are UTF-16 offsets. Only tokens outside the exact
 * replacement range survive. Equal labels must never decide token identity. */
export const editParts = (
  parts: MessagePart[],
  text: string,
  range?: TextEditRange
): MessagePart[] => {
  const previous = plainText(parts);
  if (!range)
    return text === previous ? parts : compactParts([{ type: "text", text }]);
  const { start, end } = range;
  const insertedLength = text.length - (previous.length - (end - start));
  if (
    start < 0 ||
    end < start ||
    end > previous.length ||
    insertedLength < 0 ||
    text.slice(0, start) !== previous.slice(0, start) ||
    text.slice(start + insertedLength) !== previous.slice(end)
  ) {
    // Unknown/autocorrect edits fail closed rather than assigning an ID by
    // matching identical visible labels at a different position.
    return compactParts([{ type: "text", text }]);
  }
  let offset = 0;
  const tokens: Array<{ start: number; end: number; part: MessagePart }> = [];
  parts.forEach((part) => {
    const finish = offset + plainText([part]).length;
    if (part.type === "mention" && (finish <= start || offset >= end)) {
      const shift = offset >= end ? insertedLength - (end - start) : 0;
      tokens.push({ start: offset + shift, end: finish + shift, part });
    }
    offset = finish;
  });
  let cursor = 0;
  const result: MessagePart[] = [];
  tokens.forEach((token) => {
    result.push(
      { type: "text", text: text.slice(cursor, token.start) },
      token.part
    );
    cursor = token.end;
  });
  result.push({ type: "text", text: text.slice(cursor) });
  return compactParts(result);
};
